import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of } from 'rxjs';
import { CachedContent } from '../../interfaces/cached-content';
import { Token } from '../../interfaces/token';
import { ApiConfigService } from '../../ssr-services/api-config/api-config.service';
import { CacheService } from '../../ssr-services/cache/cache.service';

export interface SocialAuthError {
  success: false;
  error: string;
}

declare global {
  interface GlobalThis {
    google: {
      accounts: {
        id: {
          initialize(config: {
            client_id: string;
            login_uri?: string;
            redirect_uri?: string;
            ux_mode?: string;
            scope?: string;
            callback(response: { credentials: string }): void;
          }): void;
          renderButton(
            element: HTMLElement,
            config: { theme: string; size: string; text: string }
          ): void;
          prompt(): void;
        };
        oauth2: {
          initCodeClient(config: {
            scope?: string;
            client_id: string;
            login_uri?: string;
            response_type?: string;
            access_type?: string;
            redirect_uri?: string;
            state?: string;
            ux_mode: string;
          }): {
            requestCode(): void;
          };
        };
      };
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class GoogleAuthService {
  protected scriptLoadStatus: Promise<boolean> | null = null;

  public scriptLoaded = false;

  protected cacheService = inject(CacheService);

  protected http = inject(HttpClient);

  protected apiConfig = inject(ApiConfigService);

  protected $document = inject(DOCUMENT);

  public async loadScript(): Promise<boolean> {
    if (this.scriptLoaded) {
      return false;
    }

    this.scriptLoadStatus ??= new Promise<boolean>((resolve, reject) => {
      if (this.$document.querySelector(`script[src="https://accounts.google.com/gsi/client"]`)) {
        resolve(true);
        return;
      }
      const script = this.$document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(true);
      script.onerror = (error) =>
        reject(
          new Error(typeof error === 'string' ? error : 'Unable to load google client script')
        );
      this.$document.head.appendChild(script);
    });

    return this.scriptLoadStatus;
  }

  /**
   * Create signin button
   * @param elementId
   */
  public initiateLogin(
    internalRedirectUrl:
      | {
          url?: string;
          queryParams?: { [key: string]: string };
        }
      | undefined = {},
    subscriberData?: { language?: string; currency?: string }
  ): void {
    const cacheContent = this.cacheService.cachedContent() as CachedContent;

    const stateContent = JSON.stringify({
      type: 'google',
      internalRedirectUrl: internalRedirectUrl || {},
      language: subscriberData?.language || '',
      currency: subscriberData?.currency || '',
    });

    const redirectUrl = `${this.apiConfig.getHost()}/auth/signin`;
    const client = (
      globalThis as typeof globalThis & {
        google: {
          accounts: {
            oauth2: {
              initCodeClient(config: {
                scope?: string;
                client_id: string;
                login_uri?: string;
                response_type?: string;
                access_type?: string;
                redirect_uri?: string;
                state?: string;
                ux_mode: string;
              }): {
                requestCode(): void;
              };
            };
          };
        };
      }
    ).google.accounts.oauth2.initCodeClient({
      client_id: cacheContent.cache.config.socialLogin.googleClientId,
      redirect_uri: redirectUrl,
      state: stateContent,
      scope: 'email profile',
      ux_mode: 'redirect',
    });

    client.requestCode();
  }

  /**
   * Exchanges code for token
   * @param code
   * @param subscriberData Optional language and currency data
   * @returns
   */
  public processGoogleCode(
    code: string,
    subscriberData?: { language?: string; currency?: string }
  ): Observable<Token | SocialAuthError> {
    return this.http
      .post<Token>(this.apiConfig.url('subscribers/signin/google'), {
        socialToken: code,
        customerId: (this.cacheService.cachedContent() as CachedContent).cache.customerId,
        socialTokenType: 'code',
        language: subscriberData?.language || '',
        currency: subscriberData?.currency || '',
      })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          // force logging for more information
          console.error('Google auth error:', err);
          const errorMessage = err.error?.message || 'Google sign-in failed. Please try again.';
          return of({ success: false, error: errorMessage } as SocialAuthError);
        })
      );
  }
}
